<template>
  <div class="staff-reports">
    <div class="forms header-container">
      <h2 class="organization-name">
        {{ selectedOrgObject && selectedOrgObject.name }}
      </h2>
      <div class="Report__Filter">
        <md-field>
          <label for="selectedDateRange">Date filter</label>
          <md-select
            @input="dateRangeChanged"
            :value="selectedDateRange"
            class="box"
          >
            <md-option value="all_time">All time</md-option>
            <md-option value="year_to_date">Year to Date</md-option>
            <md-option value="custom">Custom Date Range</md-option>
          </md-select>
        </md-field>

        <div
          v-if="isCustomDatePickerVisible"
          class="Report__Filter__DateRange"
        >
          <md-field
            class="md-override Report__Filter__DateRange-field"
            md-inline
          >
            <md-datepicker
              class="md-override Report__Filter__DateRange-picker"
              v-model="selectedStartDate"
              md-immediately
            >
              <label>Start date</label>
            </md-datepicker>
          </md-field>

          <md-field
            class="md-override Report__Filter__DateRange-field"
            md-inline
          >
            <md-datepicker
              class="md-override Report__Filter__DateRange-picker"
              v-model="selectedEndDate"
              md-immediately
            >
              <label>End date</label>
            </md-datepicker>
          </md-field>
        </div>

      </div>
    </div>

    <div class="forms">
      <md-field v-if="isStaffMember">
        <label>Select Organization Dashboard</label>
        <md-select
          @md-selected="reloadReports"
          v-model="selectedOrg"
          name="selected-org"
          id="selected-org"
          :disabled="organizations.refreshing || loading"
        >
          <md-option value="all" key="all">
            All Organizations
          </md-option>

          <md-option
            v-for="organization in organizations.list"
            :value="organization.id"
            :key="organization.id"
          >
            {{ organization.name }}
          </md-option>
        </md-select>
      </md-field>

      <md-field v-if="
            (this.selectedOrg != 'all' && user.isAdmin && !user.subOrganization && suborganizations().length) ||
              (user.isStaff && suborganizations().length)
              ">
        <label>Select Sub-organization</label>
        <md-select
          @md-selected="reloadReports"
          class="suborganizations"
          placeholder="No Sub Org"
          v-model="selectedSubOrganization"
        >

          <md-option
            v-if="isMultiSubOrgOwner()"
            key="no-suborg"
            value=""
            >No Sub Org
          </md-option >

          <md-option
            v-for="sub_org in suborganizations()"
            :key="sub_org.id"
            :value="sub_org.id"
            >{{ sub_org.name }}
          </md-option >

        </md-select>
      </md-field>

      <!-- Reports Export -->
      <div v-if="isStaffMember || showShefMetrics || showNorthwesternTab || showUpwardFundTab || showChfMetrics" class="exports">
        <md-field>
          <label>Select Dataset for Export</label>
          <md-select v-model="selectedDataset" name="selected-export" id="selected-export">
            <md-option
              v-for="dataset in exportableDatasets"
              :value="dataset.value"
              :key="dataset.value"
            >
              {{ dataset.label }}
            </md-option>
          </md-select>
        </md-field>
        <md-button
          class="md-primary"
          @click="exportDataset"
          :disabled="exporting || !selectedDataset"
        >
          <LoadSpinner v-if="exporting" />
          <i v-else>Export</i>
        </md-button>
        <md-button class="md-primary" @click="exportAll" :disabled="exporting">
          <LoadSpinner v-if="exporting" />
          <i v-else>Export All</i>
        </md-button>
      </div>
    </div>

    <LoadSpinner v-if="!report || organizations.refreshing || loading || chartsLoading" style="padding: 2rem 0" />

    <div v-if="report && !organizations.refreshing && !loading && !chartsLoading" class="stat-card-container">

      <div class="stat-card-wrapper">
        <div class="FundBalance stat-card">
          <div class="icon-and-tooltip">
            <p>Current fund balance</p>
            <font-awesome-icon :icon="['far', 'question-circle']" :style="{color: '#919EAB', 'margin-left': '0.5rem', 'margin-top': '-1rem'}">
            </font-awesome-icon>
            <md-tooltip class="tooltip" md-direction="top">Dollars available to grant</md-tooltip>
          </div>
          <h2 class="FundBalance__heading">{{ report.fundBalance | currency }}</h2>
          <p class="FundBalance__subtitle">As of {{ dateNow }}</p>
        </div>
      </div>

      <!-- Stat Cards - TODO: Factor these out to a component -->
      <div class="stat-card-wrapper">
        <div class="stacked-stat-cards">

          <div class="stacked-stat-card">
            <div class="icon-and-tooltip">
              <p>Total contributions</p>
              <font-awesome-icon :icon="['far', 'question-circle']" :style="{color: '#919EAB', 'margin-left': '0.5rem', 'margin-top': '-1rem'}">
              </font-awesome-icon>
              <md-tooltip class="tooltip" md-direction="top"><div>Total contributions received since program launch. &#10&#13 Includes seed funds and contributions via credit/debit card, payroll deduction, additional organization contributions, and other lump-sum gifts not received via card or payroll.</div></md-tooltip>
            </div>
            <h2>{{ report.amountContributed | currency }}</h2>
          </div>

          <div class="stacked-stat-card bottom">
            <div class="icon-and-tooltip">
              <p>Total disbursements</p>
              <font-awesome-icon :icon="['far', 'question-circle']" :style="{color: '#919EAB', 'margin-left': '0.5rem', 'margin-top': '-1rem'}">
              </font-awesome-icon>
              <md-tooltip class="tooltip" md-direction="top">Total dollars disbursed across all grants</md-tooltip>
            </div>
            <h2>{{ report.amountDisbursed | currency }}</h2>
          </div>


        </div>
      </div>

      <div class="stat-card-wrapper">
        <div class="stacked-stat-cards">
          <div class="stacked-stat-card">
            <div class="icon-and-tooltip">
              <p>Total number of grants</p>
              <font-awesome-icon :icon="['far', 'question-circle']" :style="{color: '#919EAB', 'margin-left': '0.5rem', 'margin-top': '-1rem'}">
              </font-awesome-icon>
              <md-tooltip class="tooltip" md-direction="top">Total number of requests that were either fully or partially funded</md-tooltip>
            </div>
            <h2>{{ report.totalApprovedRequests }}</h2>
          </div>

          <div class="stacked-stat-card bottom">
            <div class="icon-and-tooltip">
              <p>Requests pending</p>
              <font-awesome-icon :icon="['far', 'question-circle']" :style="{color: '#919EAB', 'margin-left': '0.5rem', 'margin-top': '-1rem'}">
              </font-awesome-icon>
              <md-tooltip class="tooltip" md-direction="top">Count of submitted requests that have not yet been decisioned</md-tooltip>
            </div>
            <h2>{{ report.requestsProcessing }}</h2>
          </div>

        </div>
      </div>
    </div>

    <div class="stat-container" v-if="report && !loading && !chartsLoading && !organizations.refreshing">
      <div
        class="tab-container"
        :selectedTabName="this.selectedTabName"
        @changed-tab="onChangedTab"
      >
        <div v-if="this.selectedOrg == 'all'" @click="selectTab('all')" :class="selectedTab('all')" name="allOrg">
          <div :class="this.selectedTabName == 'allOrg' ? 'selected-tab' : 'standard-tab'">ALL ORGANIZATIONS</div>
        </div>
        <div v-if="showUpwardFundTab && this.selectedOrg != 'all'" @click="selectTab('upwardFund')" :class="selectedTab('upwardFund')" name="upwardFund">
          <div :class="this.selectedTabName == 'upwardFund' ? 'selected-tab' : 'standard-tab'">UPWARD FUND</div>
        </div>
        <div v-if="showNorthwesternTab && this.selectedOrg != 'all'" @click="selectTab('northwestern')" :class="selectedTab('northwestern')" name="northwestern">
          <div :class="this.selectedTabName == 'northwestern' ? 'selected-tab' : 'standard-tab'">NORTHWESTERN</div>
        </div>
        <div v-if="!showUpwardFundTab && this.selectedOrg != 'all'" @click="selectTab('requests')" :class="selectedTab('requests')" name="requests">
          <div :class="this.selectedTabName == 'requests' ? 'selected-tab' : 'standard-tab'">REQUESTS</div>
        </div>
        <div v-if="showUpwardFundTab && this.selectedOrg != 'all'" @click="selectTab('nominations')" :class="selectedTab('nominations')" name="nominations">
          <div :class="this.selectedTabName == 'nominations' ? 'selected-tab' : 'standard-tab'">NOMINATIONS</div>
        </div>
        <div v-if="!showUpwardFundTab && this.selectedOrg != 'all'" :class="selectedTab('users')" name="users">
          <div :class="this.selectedTabName == 'users' ? 'selected-tab' : 'standard-tab'" @click="selectTab('users')">USERS</div>
        </div>
        <div v-if="!showUpwardFundTab && !showNorthwesternTab && this.selectedOrg != 'all'" @click="selectTab('contributions')" :class="selectedTab('contributions')" name="contributions">
          <div :class="this.selectedTabName == 'contributions' ? 'selected-tab' : 'standard-tab'" @click="() => this.selectedTabName = 'users'">CONTRIBUTIONS</div>
        </div>
        <div v-if="!showUpwardFundTab && this.selectedOrg != 'all'" @click="selectTab('trendData')" :class="selectedTab('trendData')" name="trendData">
          <div :class="this.selectedTabName == 'trendData' ? 'selected-tab' : 'standard-tab'">TREND DATA</div>
        </div>
      </div>

      <div class="all-organizations requests" v-if="this.selectedTabName == 'allOrg'">
        <h1 class="tab-name">All Organizations</h1>
        <div class="table-headings">
          <h2 class="header">Description</h2>
          <h2 class="header">Value</h2>
        </div>
        <div v-if="report && !loading && this.selectedTabName == 'allOrg'" class="stat-list">
          <StatDisplay
            v-for="stat in requestsDescriptions(report)"
            :description="stat.title"
            :value="stat.value"
            :formatter="stat.formatter"
            :tooltip="stat.tooltip"
            :bottomBorder="stat.bottomBorder"
            :display="stat.display"
          />
        </div>
      </div>

      <div class="requests" v-if="this.selectedTabName == 'users'">
        <h1 class="tab-name">Users</h1>
        <div class="table-headings">
          <h2 class="header">Description</h2>
          <h2 class="header">Value</h2>
        </div>
        <div v-if="report && !loading && this.selectedTabName == 'users'" class="stat-list">
          <StatDisplay
            v-for="stat in usersDescriptions(report)"
            :description="stat.title"
            :value="stat.value"
            :tooltip="stat.tooltip"
            :bottomBorder="stat.bottomBorder"
            :display="stat.display"
          />
        </div>
      </div>

      <div class="requests" v-if="this.selectedTabName == 'requests'">
        <h1 class="tab-name">Requests</h1>
        <div class="table-headings">
          <h2 class="header">Description</h2>
          <h2 class="header">Value</h2>
        </div>
        <div v-if="report && !loading && this.selectedTabName == 'requests'" class="stat-list">
          <StatDisplay
            v-for="stat in requestsDescriptions(report)"
            :description="stat.title"
            :value="stat.value"
            :formatter="stat.formatter"
            :tooltip="stat.tooltip"
            :bottomBorder="stat.bottomBorder"
            :display="stat.display"
          />
        </div>
      </div>

      <div class="requests" v-if="this.selectedTabName == 'contributions'">
        <h1 class="tab-name">Contributions</h1>
        <div class="table-headings">
          <h2 class="header">Description</h2>
          <h2 class="header">Value</h2>
        </div>
        <div v-if="report && !loading && this.selectedTabName == 'contributions'" class="stat-list">
          <StatDisplay
            v-for="stat in contributionsDescriptions(report)"
            :description="stat.title"
            :value="stat.value"
            :tooltip="stat.tooltip"
            :bottomBorder="stat.bottomBorder"
            :display="stat.display"
          />
        </div>
      </div>

      <div class="trends" v-if="this.selectedTabName == 'trendData'">
        <div class="trend-data-tab">
          <div class="trend-data-selection">
            <h1 class="tab-name trend-data">Trend Data</h1>

            <div class="trend-data">
              <md-field>
                <label>Select metric to trend</label>
                <md-select
                  v-model="selectedMetric"
                  name="selected-metric"
                  id="selected-metric"
                >
                  <md-option
                    v-for="metric in metrics"
                    :value="metric" :key="metric"
                  >
                    {{ metricNames[metric] }}
                  </md-option>
                </md-select>
              </md-field>
            </div>
            <div class="trend-data">
              <md-field>
                <label>Timeframe</label>
                <md-select
                  v-model="selectedTimeFrame"
                  name="selected-timeframe"
                  id="selected-timeframe"
                >
                  <md-option value="monthly" key="monthly">By Month</md-option>
                  <md-option value="yearly" key="yearly">By Year</md-option>
                </md-select>
              </md-field>
            </div>
          </div>
          <Chart :key="chartKey" class="chart" :title="metricNames[selectedMetric]" :chartData="trendChart" :isDollarSignNeeded="metricPrefixes[selectedMetric]">
          </Chart>
        </div>
      </div>

      <!-- Northwestern reporting tab -->
      <div class="requests" v-if="showNorthwesternTab && this.selectedTabName == 'northwestern'">
        <h1 class="tab-name">Northwestern</h1>
        <div class="table-headings">
          <h2 class="header">Description</h2>
          <h2 class="header">Value</h2>
        </div>
        <div v-if="report && !loading && this.selectedTabName == 'northwestern'" class="stat-list">
          <StatDisplay
            v-for="stat in northwesternDescriptions(report)"
            :description="stat.title"
            :value="stat.value"
            :tooltip="stat.tooltip"
            :bottomBorder="stat.bottomBorder"
            :display="stat.display"
          />
        </div>
      </div>

      <!-- Upward Fund reporting tab -->
      <div class="requests" v-if="showUpwardFundTab && this.selectedTabName == 'upwardFund'">
        <h1 class="tab-name">Upward Fund</h1>
        <div class="table-headings">
          <h2 class="header">Description</h2>
          <h2 class="header">Value</h2>
        </div>
        <div v-if="report && !loading && this.selectedTabName == 'upwardFund'" class="stat-list">
          <StatDisplay
            v-for="stat in upwardFundDescriptions(report)"
            :description="stat.title"
            :value="stat.value"
            :tooltip="stat.tooltip"
            :bottomBorder="stat.bottomBorder"
            :display="stat.display"
          />
        </div>

        <!-- UF nominations -->
        <h2 class="tab-name">Nominations</h2>
        <div class="table-headings">
          <h2 class="header">Description</h2>
          <h2 class="header">Value</h2>
        </div>
        <div v-if="nominationsReport && !loading && this.selectedTabName == 'upwardFund'" class="stat-list">
          <StatDisplay
            v-for="stat in nominationsDescriptions(nominationsReport)"
            :description="stat.title"
            :value="stat.value"
            :tooltip="stat.tooltip"
            :bottomBorder="stat.bottomBorder"
            :display="stat.display"
          />

          <!-- Declined nominees -->
          <div class="declined-nominees card-shape">
            <div class="collapsible-header" @click="toggleShowDeclinedNominees">
              <h2>Declined Nominees</h2>
              <div class="header-icon">
                <font-awesome-icon
                  v-if="!showDeclinedNominees"
                  class="arrow"
                  :icon="['fa', 'chevron-down']"
                  :style="{ fontSize: '1rem' }"
                />
                <font-awesome-icon
                  v-else
                  class="arrow"
                  :icon="['fa', 'chevron-up']"
                  :style="{ fontSize: '1rem' }"
                />
              </div>
            </div>
            <template v-if="report['declinedNomineesList'].length && showDeclinedNominees">
              <div class="table-headings no-margins" >
                <h2 class="header">Name</h2>
                <h2 class="header">Nominated By</h2>
                <h2 class="header">Declined Date</h2>
              </div>
              <div class="nominee-list">
                <div v-for="nomination in report['declinedNomineesList']" class="nominee">
                  <div class="stat-description-wrapper">
                    <div class="stat-description">{{ nomination['fullName'] }}</div>
                  </div>
                  <div class="stat-value">
                    {{ nomination['nominatedBy'] }} ({{ nomination['nominatorSubOrganization'] }})
                  </div>
                  <div class="stat-value">
                    {{ nomination['lastEdited'] | formatDateShort($language.current) }}
                  </div>
                </div>
              </div>
            </template>
            <div v-else-if="!report['declinedNomineesList'].length && showDeclinedNominees" class="no-data">
              There are no declined nominations for the filtered date range.
            </div>
          </div>

          <!-- Money due to nominators -->
          <div class="money-due-to-nominators card-shape">
            <div class="collapsible-header" @click="toggleShowMoneyDueToNominators">
              <h2>Money Due to Nominators</h2>
              <div class="header-icon">
                <font-awesome-icon
                  v-if="!showMoneyDueToNominators"
                  class="arrow"
                  :icon="['fa', 'chevron-down']"
                  :style="{ fontSize: '1rem' }"
                />
                <font-awesome-icon
                  v-else
                  class="arrow"
                  :icon="['fa', 'chevron-up']"
                  :style="{ fontSize: '1rem' }"
                />
              </div>
            </div>
            <template v-if="Object.keys(report['moneyDueToNominators']).length && showMoneyDueToNominators">
              <div class="table-headings no-margins" >
                <h2 class="header">Name</h2>
                <h2 class="header"># of Nominees</h2>
                <h2 class="header">Amount Due</h2>
              </div>
              <div class="nominator-list">
                <div v-for="nominatorId in Object.keys(report['moneyDueToNominators'])" class="nominator">
                  <div class="stat-description-wrapper">
                    <div class="stat-description">{{ report['moneyDueToNominators'][nominatorId]['fullName'] }}</div>
                  </div>
                  <div class="stat-value">
                    {{ report['moneyDueToNominators'][nominatorId]['count'] }}
                  </div>
                  <div class="stat-value">
                    {{ report['moneyDueToNominators'][nominatorId]['amountOwed'] | currency }}
                  </div>
                </div>
              </div>
            </template>
            <div v-else-if="!Object.keys(report['moneyDueToNominators']).length && showMoneyDueToNominators" class="no-data">
              No money due to nominators for the current filtered date range.
            </div>
          </div>
        </div>

        <!-- UF applications -->
        <h2 class="tab-name">Applications</h2>
        <div class="table-headings">
          <h2 class="header">Description</h2>
          <h2 class="header">Value</h2>
        </div>
        <div v-if="nominationsReport && !loading && this.selectedTabName == 'upwardFund'" class="stat-list">
          <StatDisplay
            v-for="stat in applicationsDescriptions(nominationsReport)"
            :description="stat.title"
            :value="stat.value"
            :tooltip="stat.tooltip"
            :bottomBorder="stat.bottomBorder"
            :display="stat.display"
          />
        </div>

        <!-- UF participant summary -->
        <h2 class="tab-name">Participant Summary</h2>
        <div class="table-headings">
          <h2 class="header">Description</h2>
          <h2 class="header">Value</h2>
        </div>
        <div v-if="!loading && this.selectedTabName == 'upwardFund'" class="stat-list">
          <StatDisplay
            v-for="stat in participantSummaryDescriptions(participantSummaryReport)"
            :description="stat.title"
            :value="stat.value"
            :tooltip="stat.tooltip"
            :bottomBorder="stat.bottomBorder"
            :display="stat.display"
          />

          <!-- Enrolled participants -->
          <div class="enrolled-participants card-shape">
            <div class="collapsible-header" @click="toggleShowEnrolledParticipants">
              <h2>Enrolled Participants</h2>
              <div class="header-icon">
                <font-awesome-icon
                  v-if="!showEnrolledParticipants"
                  class="arrow"
                  :icon="['fa', 'chevron-down']"
                  :style="{ fontSize: '1rem' }"
                />
                <font-awesome-icon
                  v-else
                  class="arrow"
                  :icon="['fa', 'chevron-up']"
                  :style="{ fontSize: '1rem' }"
                />
              </div>
            </div>
            <template v-if="report['enrolledParticipantsList'].length && showEnrolledParticipants">
              <div class="table-headings no-margins" >
                <h2 class="header">Name</h2>
                <h2 class="header">Date Enrolled</h2>
              </div>
              <div class="participant-list">
                <div v-for="participant in report['enrolledParticipantsList']" class="participant">
                  <div class="stat-description-wrapper">
                    <div class="stat-description">{{ participant['name'] }}</div>
                  </div>
                  <div class="stat-value">
                    {{ participant['enrolledDate'] }}
                  </div>
                </div>
              </div>
            </template>
            <div v-else-if="!report['enrolledParticipantsList'].length && showEnrolledParticipants" class="no-data">
              There are no enrolled participants for the filtered date range.
            </div>
          </div>

          <!-- At risk participants (current) -->
          <div class="at-risk-participants card-shape">
            <div class="collapsible-header" @click="toggleShowAtRiskParticipants">
              <h2>At Risk Participants (Current)</h2>
              <div class="header-icon">
                <font-awesome-icon
                  v-if="!showAtRiskParticipants"
                  class="arrow"
                  :icon="['fa', 'chevron-down']"
                  :style="{ fontSize: '1rem' }"
                />
                <font-awesome-icon
                  v-else
                  class="arrow"
                  :icon="['fa', 'chevron-up']"
                  :style="{ fontSize: '1rem' }"
                />
              </div>
            </div>
            <template v-if="report['atRiskParticipants'].length && showAtRiskParticipants">
              <div class="table-headings no-margins" >
                <h2 class="header">Name</h2>
                <h2 class="header">Nominated By</h2>
              </div>
              <div class="participant-list">
                <div v-for="participant in report['atRiskParticipants']" class="participant">
                  <div class="stat-description-wrapper">
                    <div class="stat-description">{{ participant['fullName'] }}</div>
                  </div>
                  <div class="stat-value">
                    {{ participant['nominatedBy'] }}
                  </div>
                </div>
              </div>
            </template>
            <div v-else-if="!report['atRiskParticipants'].length && showAtRiskParticipants" class="no-data">
              No participants are at risk for the filtered date range.
            </div>
          </div>

          <!-- At risk participants (past) -->
          <div class="at-risk-participants card-shape">
            <div class="collapsible-header" @click="toggleShowAtRiskParticipantsPast">
              <h2>At Risk Participants (Past)</h2>
              <div class="header-icon">
                <font-awesome-icon
                  v-if="!showAtRiskParticipantsPast"
                  class="arrow"
                  :icon="['fa', 'chevron-down']"
                  :style="{ fontSize: '1rem' }"
                />
                <font-awesome-icon
                  v-else
                  class="arrow"
                  :icon="['fa', 'chevron-up']"
                  :style="{ fontSize: '1rem' }"
                />
              </div>
            </div>
            <template v-if="report['atRiskParticipantsPast'].length && showAtRiskParticipantsPast">
              <div class="table-headings no-margins" >
                <h2 class="header">Name</h2>
                <h2 class="header">Months</h2>
                <h2 class="header">Nominated By</h2>
              </div>
              <div class="participant-list">
                <div v-for="participant in report['atRiskParticipantsPast']" class="participant">
                  <div class="stat-description-wrapper">
                    <div class="stat-description">{{ participant['fullName'] }}</div>
                  </div>
                  <div class="stat-value">
                    {{ participant['programMonths'].join(', ') }}
                  </div>
                  <div class="stat-value">
                    {{ participant['nominatedBy'] }}
                  </div>
                </div>
              </div>
            </template>
            <div v-else-if="!report['atRiskParticipantsPast'].length && showAtRiskParticipantsPast" class="no-data">
              No participants are at risk for the filtered date range.
            </div>
          </div>
        </div>
      </div>

      <!-- Upward Fund nominations tab -->
      <div class="nominations" v-if="showUpwardFundTab && this.selectedTabName == 'nominations'">
        <h1 class="tab-name">Nominations</h1>

        <div class="nominator-filter">
          <md-field>
            <label>Select a Nominator</label>
            <md-select
              @md-selected="reloadNominatorReport"
              v-model="selectedNominator"
            >
              <md-option value="">All</md-option>
              <md-option v-for="nominator in nominationsReport['nominators']" :key="nominator.id"
              :value="nominator.id">
                {{ nominator.firstName }} {{ nominator.lastName }}
              </md-option>
            </md-select>
          </md-field>
        </div>

        <template v-if="!nominatorReportLoading">
          <div class="table-headings">
            <h2 class="header">Description</h2>
            <h2 class="header">Value</h2>
          </div>
          <div v-if="nominationsReport && !loading && this.selectedTabName == 'nominations'" class="stat-list">
            <StatDisplay
              v-for="stat in nominationsReportDescriptions(nominationsReport)"
              :description="stat.title"
              :value="stat.value"
              :tooltip="stat.tooltip"
              :bottomBorder="stat.bottomBorder"
              :display="stat.display"
            />
          </div>
        </template>
        <LoadSpinner :color="black" v-else />
      </div>

      <div class="all-charts">
        <div class="bar-charts card-shape" v-if="!chartsLoading && Object.keys(charts).length && !organizations.refreshing && this.selectedTabName == 'upwardFund'">
          <h2>Goal Progression Chart</h2>
          <md-field class="goal-question">
            <label>Select Goal Question</label>
            <md-select
              v-model="selectedQuestion"
              @input="selectedQuestionChanged"
              class="selected-question"
              name="selected-question"
              id="selected-question"
              :disabled="loading"
            >
              <md-option
                v-for="question in goalProgressQuestions"
                :value="question"
                :key="question"
              >
                {{ question }}
              </md-option>
            </md-select>
          </md-field>
          <BarChart :key="barChartKey" class="barChart" :barChartData="goalProgressionData" :selectedQuestion.sync="selectedQuestion"/>
        </div>

        <div class="bar-charts card-shape" v-if="!chartsLoading && Object.keys(charts).length && !organizations.refreshing && this.selectedTabName == 'upwardFund'">
          <h2>Program Month Chart</h2>
          <ProgramMonthBarChart class="barChart" :barChartData="programMonthData" />
        </div>

        <div class="bar-charts card-shape" v-if="!chartsLoading && Object.keys(charts).length && !organizations.refreshing && this.selectedTabName == 'upwardFund'">
          <h2>Participant Dropout Chart</h2>
          <ParticipantDropoutBarChart class="barChart" :barChartData="participantDropoutData" />
        </div>

        <div class="bar-charts card-shape" v-if="!chartsLoading && Object.keys(charts).length && !organizations.refreshing && this.selectedTabName == 'upwardFund'">
          <h2>Participant Dropout Reason Chart</h2>
          <ParticipantDropoutReasonBarChart class="barChart" :barChartData="participantDropoutReasonData" />
        </div>

        <div class="charts">
          <div class="first-col-charts" v-if="!chartsLoading && Object.keys(charts).length && !organizations.refreshing && (this.selectedTabName == 'requests' || this.selectedTabName == 'allOrg')">
            <div v-for="(chart, index) in [charts['events'], charts['decline']]">
              <div class="new-chart">
                <div class="new-chart__title_and_tooltip">
                  <h6 class="new-chart__title">{{ chart.title }}</h6>
                  <div class="icon-and-tooltip">
                    <font-awesome-icon :icon="['far', 'question-circle']" :style="{color: '#919EAB', 'margin-left': '0.5rem'}">
                    </font-awesome-icon>
                    <md-tooltip class="tooltip" md-direction="top">{{chart.tooltip}}</md-tooltip>
                  </div>
                </div>
                <span class="new-chart__subtitle">{{ chart.subtitle }}</span>

                <div class="chart-pill" v-for="(value, index) in chart.data.datasets[0].data">
                  <div class="pill-percent-wrapper">
                    <div class="pill-title-wrapper">
                      <div class="pill-title">{{ chart.data.labels[index] }}</div>
                    </div>
                    <div class="pill-percentage">{{value}}%</div>
                  </div>
                  <div class="pill">
                    <div v-if="value > 0" class="pill-fill" :style="{width: `${value}%`, 'background-color': `${chart.data.datasets[0].backgroundColor}`, 'border-color': `${chart.data.datasets[0].backgroundColor}`}" >
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="first-col-charts" v-if="!chartsLoading && Object.keys(charts).length && !organizations.refreshing && this.selectedTabName == 'upwardFund'">
            <div v-for="(chart, index) in [charts['nominatingSubOrgs']]">
              <div class="new-chart">
                <div class="new-chart__title_and_tooltip">
                  <h6 class="new-chart__title">{{ chart.title }}</h6>
                  <div class="icon-and-tooltip">
                    <font-awesome-icon :icon="['far', 'question-circle']" :style="{color: '#919EAB', 'margin-left': '0.5rem'}">
                    </font-awesome-icon>
                    <md-tooltip class="tooltip" md-direction="top">{{chart.tooltip}}</md-tooltip>
                  </div>
                </div>
                <span class="new-chart__subtitle">{{ chart.subtitle }}</span>

                <div class="chart-pill" v-for="(value, index) in chart.data.datasets[0].data">
                  <div class="pill-percent-wrapper">
                    <div class="pill-title-wrapper">
                      <div class="pill-title">{{ chart.data.labels[index] }}</div>
                    </div>
                    <div class="pill-percentage">{{ chart.data.values[index] }}</div>
                  </div>
                  <div class="pill">
                    <div v-if="value > 0" class="pill-fill" :style="{width: `${value}%`, 'background-color': `${chart.data.datasets[0].backgroundColor}`, 'border-color': `${chart.data.datasets[0].backgroundColor}`}" >
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="second-col-charts" v-if="!chartsLoading && Object.keys(charts).length && !organizations.refreshing && (this.selectedTabName == 'requests' || this.selectedTabName == 'allOrg')">
            <div v-for="(chart, index) in [charts['expenses']]">
              <div class="new-chart">
                <div class="new-chart__title_and_tooltip">
                  <h6 class="new-chart__title">{{ chart.title }}</h6>
                  <div class="icon-and-tooltip">
                    <font-awesome-icon :icon="['far', 'question-circle']" :style="{color: '#919EAB', 'margin-left': '0.5rem'}">
                    </font-awesome-icon>
                    <md-tooltip class="tooltip" md-direction="top">{{chart.tooltip}}</md-tooltip>
                  </div>
                </div>
                <span class="new-chart__subtitle">{{ chart.subtitle }}</span>

                <div class="chart-pill" v-for="(value, index) in chart.data.datasets[0].data">
                  <div class="pill-percent-wrapper">
                    <div class="pill-title-wrapper">
                      <div class="pill-title">{{ chart.data.labels[index] }}</div>
                    </div>
                    <div class="pill-percentage">{{value}}%</div>
                  </div>
                  <div class="pill">
                    <div v-if="value > 0" class="pill-fill" :style="{width: `${value}%`, 'background-color': `${chart.data.datasets[0].backgroundColor}`, 'border-color': `${chart.data.datasets[0].backgroundColor}`}" >
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Organization from '@/services/organizations'
import SelectOrganization from '@/services/selectOrganizations'
import Event from '@/services/events'
import Report from '@/services/reporting'
import OrgChart from '@/services/charts'
import CanaryDashboard from '@/services/canaryDashboard'
import CollectionManager from '@/services/collectionManager'
import { LoadSpinner, Chart, BarChart, ProgramMonthBarChart, ParticipantDropoutBarChart, ParticipantDropoutReasonBarChart } from '@/components'
import Tab from '@/components/Tab'
import TabContainer from '@/components/TabContainer'
import StatDisplay from '@/components/staff/StatDisplay'
import { analyticsMixin } from '@/services/googleAnalytics'
import Pagination from '@/services/pagination'
import { mapState } from 'vuex'
import AdminPermissions from '@/services/adminPermissions'

const decline_reason_labels = {
  'DOCUMENTATION_INSUFFICIENT_OR_UNCLEAR': 'Insufficient documentation',
  'DUPLICATE': 'Duplicate',
  'OVER_INDIVIDUAL_LIMIT': 'Over individual limit',
  'WITHDRAWN': 'Withdrawn',
  'SUSPECTED_FRAUD': 'Suspected fraud',
  'NOT_ELIGIBLE': 'Not eligible',
  'OTHER': 'Other',
  'NOT_WITHIN_LOOKBACK_PERIOD': 'Not within lookback period',
  'LOOKBACK_PERIOD': 'Lookback period',
  'NON_QUALIFYING_EVENT': 'Non-qualifying event',
  'TEST': 'Test account',
  'INCOME_TOO_LOW': 'Income too low',
  'INCOME_TOO_HIGH': 'Income too high',
  'NOT_PERMANENTLY_HOUSED': 'Not permanently housed',
  'LIVING_WITH_FAMILY_FRIENDS_NOT_ON_LEASE': 'Living with family friends; not on lease',
  'NO_PROOF_OF_HOUSING': 'No proof of housing',
  'NOT_IN_SOCAL': 'Not in SoCal',
  'NOT_A_GOALS_FIT': 'Not a goals fit',
  'TOO_EARLY_IN_UNDERGRAD': 'Too early in undergrad',
  'NO_RESPONSE_FROM_NOMINATOR_OR_NOMINEE': 'No response from nominator or nominee',
  'UNSPECIFIED': 'Unspecified',
  'DOCUMENTATION_NO_RESPONSE': 'Documentation- No response',
  'NOT_A_COVERED_EVENT': 'Not a covered event',
  'NONCOMPLIANT': 'Non-compliant',
}

const decline_reason_details_labels = {
  'DOCUMENTATION_INSUFFICIENT_OR_UNCLEAR': 'Insufficient documentation',
  'OTHER': 'Other',
  'LOOKBACK_PERIOD': 'Lookback period',
  'NON_QUALIFYING_EVENT': 'Non-qualifying event',
  'NO_LONGER_EMPLOYEE': 'No longer an employee',
  'DO_NOT_MEET_TENURE_REQUIREMENT': 'Tenure requirement',
}

const metrics = [
  'goal_progression',
  'amount_contributed',
  'amount_disbursed',
  'total_approved_requests',
  'amount_requested',
  'requests_received',
  'requests_fully_approved',
  'requests_partially_approved',
  'requests_declined',
  'onboarded_users',
  'onboarded_active_users',
  'credit_debit_contributions',
  'payroll_contributions',
]

const metricNames = {
  'amount_contributed': 'Total contributions',
  'amount_disbursed': 'Total disbursements',
  'total_approved_requests': 'Total number of grants',
  'amount_requested': 'Total requested',
  'requests_received': 'Requests received',
  'requests_fully_approved': 'Requests fully funded',
  'requests_partially_approved': 'Requests partially approved',
  'requests_declined': 'Requests declined',
  'onboarded_users': 'Onboarded users',
  'onboarded_active_users': 'Onboarded active users',
  'credit_debit_contributions': 'Credit/debit card contributions',
  'payroll_contributions': 'Payroll contributions',
  'unique_contributors': 'Unique contributors',
}

const metricPrefixes = {
  'amount_contributed': true,
  'amount_disbursed': true,
  'total_approved_requests': false,
  'amount_requested': true,
  'requests_received': false,
  'requests_fully_approved': false,
  'requests_partially_approved': false,
  'requests_declined': false,
  'onboarded_users': false,
  'onboarded_active_users': false,
  'credit_debit_contributions': true,
  'payroll_contributions': true,
  'unique_contributors': false,
}

const CLIENTS_SELF_MANAGING_PAYROLL_DONATIONS = ["Boston Beer Company", "Partners For Quality"]

export default {
  name: 'Reports',
  mixins: [analyticsMixin],
  components: {
    LoadSpinner,
    Chart,
    BarChart,
    Tab,
    TabContainer,
    StatDisplay,
    ProgramMonthBarChart,
    ParticipantDropoutBarChart,
    ParticipantDropoutReasonBarChart,
  },
  data() {
    return {
      organizations: CollectionManager.create({
        ModelClass: SelectOrganization,
        pagination: Pagination.create({ pageSize: 100 }),
      }),
      metrics: metrics,
      metricNames: metricNames,
      metricPrefixes: metricPrefixes,
      clientsSelfManagingPayrollDonations: CLIENTS_SELF_MANAGING_PAYROLL_DONATIONS,

      selectedOrg: null,
      selectedSubOrganization: null,
      report: null,
      nominationsReport: null,
      participantSummaryReport: null,
      selectedDataset: null,

      selectedDateRange: "all_time",
      isCustomDatePickerVisible: false,
      selectedStartDate: null, // forces md-datepicker to Date model
      selectedEndDate: null, // forces md-datepicker to Date model
      reportFilter: null,

      selectedTabName: null,
      selectedTimeFrame: 'monthly',
      exporting: false,
      loading: false,
      charts: {},
      chartsLoading: false,
      trendDataLabels: [],
      trendDataDataset: [],
      goalProgressionData: {},
      programMonthData: {},
      participantDropoutData: {},
      participantDropoutReasonData: {},
      selectedQuestion: "I feel that I am regularly able to afford basic living expenses with my current income.",
      goalProgressQuestions: [
          "I feel that I am regularly able to afford basic living expenses with my current income.",
          "I feel secure with my employment income and able to pursue my personal / professional goals.",
          "I am currently able to put money into savings.",
          "With additional education / certification, there is an opportunity to advance at my current job, or a new job that I am pursuing.",
          "I feel secure with my housing.",
      ],
      chartKey: 1,
      barChartKey: 1,
      selectedMetric: 'amount_requested',
      trendChart: {
        name: 'Trend Data',
        title: metricNames[this.selectedMetric],
        data: {
          datasets: [
            {
              data: [],
            },
          ],
        },
      },
      showEnrolledParticipants: false,
      showDeclinedNominees: false,
      showMoneyDueToNominators: false,
      showAtRiskParticipants: false,
      showAtRiskParticipantsPast: false,
      selectedNominator: '',
      nominatorReportLoading: false,
    }
  },
  created() {
    this.refresh()
  },
  methods: {
    selectedQuestionChanged() {
      this.barChartKey += 1
    },
    suborganizations() {
      if (this.user.isStaff) {
        return (this.selectedOrgObject && this.selectedOrgObject.subOrganizations) || []
      }

      if (this.user.isAdmin && !this.user.subOrganization) {
        return this.organizationDetails.subOrganizations || []
      }

      return []
    },
    requestsDescriptions(report) {
      let descriptors = [
        {
          title: "Total requested",
          value: report['amountRequested'],
          formatter: this.$options.filters.currency,
          tooltip: "Total dollars requested across all submitted requests",
          display: true
        },
        {
          title: this.showUpwardFundTab ? 'Applications received' : 'Requests received',
          value: report["requestsReceived"],
          tooltip: this.showUpwardFundTab ? "Count of applications received" : "Count of grant requests submitted",
          bottomBorder: this.showUpwardFundTab ? false : true,
          display: true
        },
      ]

      // UF delineation work foir SRs received
      if (this.showUpwardFundTab) {
        descriptors.push({
          title: 'Stipend Requests received',
          value: report["stipendRequestsReceived"],
          tooltip: "Count of Stipend Requests received",
          bottomBorder: true,
          display: true
        })
      }

      // SHEF custom reporting
      if (this.showShefMetrics) {
        descriptors.push({
          title: '# of Certificated',
          value: report["requestsCertificated"],
          tooltip: "Count of requests from a certificated staff member.",
          bottomBorder: false,
          display: true
        })
        descriptors.push({
          title: '# of Classified',
          value: report["requestsClassified"],
          tooltip: "Count of requests from a classified staff member.",
          bottomBorder: true,
          display: true
        })
      }

      descriptors = descriptors.concat([
        {
          title: 'Requests fully funded',
          value: report['requestsFullyApproved'],
          tooltip: "Count of requests where the requestor received the full dollar amount requested",
          display: true
        },
        {
          title:'Requests partially funded',
          value: report['requestsPartiallyApproved'],
          tooltip: "Count of requests where the requestor received less than the dollar amount requested. This could be because (a) they asked for more than the grant limit or more than they were eligible to receive, given their previous grant activity; or (b) their documentation was not sufficient to support funding more than what they were granted.",
          display: true
        },
        {
          title: 'Requests partially funded, pending',
          value: report['requestsPartiallyApprovedPending'],
          tooltip: "Count of requests where an initial portion of the grant has been funded, and we are awaiting additional information from the requestor to determine whether we can fund a larger portion of the request",
          display: true
        },
        {
          title: this.showUpwardFundTab ? 'Applications declined' : 'Requests declined',
          value: report['requestsDeclined'],
          tooltip: this.showUpwardFundTab ? "Count of applications declined" : "Count of requests that were not funded at all",
          bottomBorder: this.showUpwardFundTab ? false : true,
          display: true
        },
      ])

      // UF delineation work for SRs declined
      if (this.showUpwardFundTab) {
        descriptors.push({
          title: 'Stipend Requests Declined',
          value: report["stipendRequestsDeclined"],
          tooltip: "Count of Stipend Requests declined",
          bottomBorder: true,
          display: true
        })
      }

      descriptors = descriptors.concat([
        {
          title: "Requests referred",
          value: report['referredRequestsFullyApproved'],
          tooltip: "Count of grant requests referred to sponsoring organization",
          display: this.isReferralClient
        },
        {
          title: "Referred fully funded",
          value: report['referredRequestsFullyApproved'],
          tooltip: "Count of referred requests where the requestor received the full dollar amount requested",
          display: this.isReferralClient
        },
        {
          title: "Referred partially funded",
          value: report["referredRequestsPartiallyApproved"],
          tooltip: "Count of referred requests where the requestor received less than the dollar amount requested",
          display: this.isReferralClient,
        },
        {
          title: "Referred declined",
          value: report["referredRequestsDeclined"],
          tooltip: "Count of referred requests that were not funded at all",
          display: this.isReferralClient,
          bottomBorder: true,
        },
      ])

      return descriptors
    },
    usersDescriptions(report) {
      return [
        {
          title: 'Eligible users',
          value: report["eligibleUsers"],
          tooltip: "Total number of eligible individuals",
          bottomBorder: true,
          display: true,
        },
        {
          title: 'Onboarded users',
          value: report["onboardedUsers"],
          tooltip: "Total number of individuals who have created an account",
          bottomBorder: true,
          display: true,
        },
        {
          title: 'Onboarded active users',
          value: report["onboardedActiveUsers"],
          tooltip: "Number of onboarded individuals who are still eligible to participate",
          bottomBorder: true,
          display: true,
        },
        {
          title: 'Onboarded inactive users',
          value: report["onboardedInactiveUsers"],
          tooltip: "Number of onboarded individuals who have become ineligible to participate since onboarding",
          bottomBorder: true,
          display: true,
        },
        {
          title: "Unique requestors",
          value: report["uniqueRequestors"],
          tooltip: "Count of individuals to submit at least one grant request",
          bottomBorder: true,
          display: true,
        },
        {
          title: "Unique grantees",
          value: report["uniqueGrantees"],
          tooltip: "Unique count of requestors to receive at least one grant",
          bottomBorder: true,
          display: true,
        },
      ]
    },
    contributionsDescriptions(report) {
      return [
        {
          title: "Seed funds",
          value: this.$options.filters.currency(report["startingFundBalance"]),
          tooltip: "Seed funds contributed by the sponsoring organization, and any other donors, to launch the Grant Circle",
          display: true,
        },
        {
          title: "Organization contributions (non-seed)",
          value: this.$options.filters.currency(report["organizationContributions"]),
          tooltip: "Funds contributed by the sponsoring organization after program launch (that is, non-seed)",
          display: true,
        },
        {
          title: "Other contributions (non-seed, non-card, non-payroll)",
          value: this.$options.filters.currency(report["otherContributions"]),
          tooltip: "Lump-sum contributions not received via card or payroll, from an entity other than the sponsoring organization",
          bottomBorder: true,
          display: true,
        },
        {
          title: "Credit/debit card contributions",
          value: this.$options.filters.currency(report["creditDebitContributions"]),
          tooltip: "Total contributions received via credit/debit card",
          display: true,
        },
        {
          title: "Median one-time card contribution",
          value: this.$options.filters.currency(report["medianOneTimeCardContribution"]),
          tooltip: "Median one-time credit/debit card contribution amount",
          display: true,
        },
        {
          title: "One-time card contributions",
          value: report["oneTimeCardContributors"],
          tooltip: "Count of credit/debit card contributions",
          display: true,
        },
        {
          title: "Median active recurring card contribution",
          value: this.$options.filters.currency(report["medianActiveRecurringCardContributions"]),
          tooltip: "Median recurring credit/debit card contribution amount for those that are still active",
          display: true,
        },
        {
          title: "Active recurring card contributors",
          value: report["activeRecurringCardContributors"],
          tooltip: "Count of unique contributors with an active recurring credit/debit card contribution",
          display: true,
          bottomBorder: true,
        },
        {
          title: "Payroll contributions",
          value: this.$options.filters.currency(report["payrollContributions"]),
          tooltip: "Total contributions received via payroll deduction",
          display: this.selectedOrgObject.allowsPayrollRequests || this.isSelfManagedPayrollDonations,
        },
        {
          title: "Median one-time payroll contribution",
          value: this.$options.filters.currency(report["medianOneTimePayrollContributions"]),
          tooltip: "Median one-time payroll contribution amount",
          display: this.selectedOrgObject.allowsPayrollRequests || this.isSelfManagedPayrollDonations,
        },
        {
          title: "One-time payroll contributions",
          value: report["oneTimePayrollContributions"],
          tooltip: "Count of one-time payroll contributions",
          display: this.selectedOrgObject.allowsPayrollRequests || this.isSelfManagedPayrollDonations,
        },
        {
          title: "Median recurring payroll contribution",
          value: this.$options.filters.currency(report["medianRecurringPayrollContributions"]),
          tooltip: "Median recurring payroll contribution amount",
          display: this.selectedOrgObject.allowsPayrollRequests || this.isSelfManagedPayrollDonations,
        },
        {
          title: "Recurring payroll contributors",
          value: report["recurringPayrollContributions"],
          tooltip: "Count of recurring payroll contributors (all-time)",
          display: this.selectedOrgObject.allowsPayrollRequests || this.isSelfManagedPayrollDonations,
          bottomBorder: true,
        },
        {
          title: "Unique contributors",
          value: report["uniqueContributors"],
          tooltip: "Count of individuals or entities that have made at least one donation via credit/debit card, payroll deduction, or lump-sum contribution (excluding sponsoring organization) de-duped by email and/or unique ID.",
          bottomBorder: true,
          display: true,
        },
      ]
    },
    upwardFundDescriptions(report) {
      return [
        {
          title: "# of Enrolled Participants",
          value: report["enrolledParticipantsCount"],
          tooltip: "The number of enrolled participants in the Upward Fund, meaning their application was approved.",
          bottomBorder: false,
          display: true,
        },
        {
          title: "# of Active Nominators",
          value: report["activeNominatorsCount"],
          tooltip: "The number of nominators with an enrolled participant.",
          bottomBorder: false,
          display: true,
        },
        {
          title: "# of At-risk Participants",
          value: report["numberOfAtRiskParticipants"],
          tooltip: "The number of at-risk participants.",
          bottomBorder: true,
          display: true,
        },
      ]
    },
    northwesternDescriptions(report) {
      return [
        {
          title: "# of incentive payments",
          value: report["incentivePayments"][0],
          tooltip: "The number of incentive payments sent.",
          bottomBorder: false,
          display: true,
        },
        {
          title: "Incentive payments total",
          value: this.$options.filters.currency(report["incentivePayments"][1]),
          bottomBorder: true,
          display: true,
        },
        {
          title: "# of treatment payments",
          value: report["treatmentPayments"][0],
          tooltip: "The number of treat payments sent.",
          bottomBorder: false,
          display: true,
        },
        {
          title: "Treatment payments total",
          value: this.$options.filters.currency(report["treatmentPayments"][1]),
          bottomBorder: true,
          display: true,
        },
        {
          title: "# of follow-up payments",
          value: report["followUpPayments"][0],
          tooltip: "The number of follow-up survey payments sent.",
          bottomBorder: false,
          display: true,
        },
        {
          title: "Follow-up payments total",
          value: this.$options.filters.currency(report["followUpPayments"][1]),
          bottomBorder: true,
          display: true,
        },
      ]
    },
    nominationsDescriptions(nominationsReport) {
      return [
        {
          title: "# of Nominations",
          value: nominationsReport["ufTotalNumberOfNominations"],
          tooltip: "The total number of nominations.",
          bottomBorder: false,
          display: true,
        },
        {
          title: "# of Nominations Under Review",
          value: nominationsReport["ufNumberOfNominationsUnderReview"],
          tooltip: "Number of nominations currently under review.",
          bottomBorder: false,
          display: true,
        },
        {
          title: "# of Nominations In Progress",
          value: nominationsReport["ufNumberOfNominationsInProgress"],
          tooltip: "Number of nominations currently in progress.",
          bottomBorder: false,
          display: true,
        },
        {
          title: "# of Nominations Denied",
          value: nominationsReport["ufNumberOfNominationsDenied"],
          tooltip: "Number of nominations denied.",
          bottomBorder: false,
          display: true,
        },
        {
          title: "# of Nominations Approved",
          value: nominationsReport["ufNumberOfNominationsApproved"],
          tooltip: "Number of nominations approved.",
          bottomBorder: false,
          display: true,
        },
      ]
    },
    applicationsDescriptions(nominationsReport) {
      return [
        {
          title: "# of Applications",
          value: nominationsReport["ufTotalNumberOfApplications"],
          tooltip: "The total number of applications.",
          bottomBorder: false,
          display: true,
        },
        {
          title: "# of Applications Under Review",
          value: nominationsReport["ufNumberOfApplicationsUnderReview"],
          tooltip: "Number of applications currently under review.",
          bottomBorder: false,
          display: true,
        },
        {
          title: "# of Applications In Progress",
          value: nominationsReport["ufNumberOfApplicationsInProgress"],
          tooltip: "Number of applications currently under review.",
          bottomBorder: false,
          display: true,
        },
        {
          title: "# of Applications Denied",
          value: nominationsReport["ufNumberOfApplicationsDenied"],
          tooltip: "Number of applications denied.",
          bottomBorder: false,
          display: true,
        },
        {
          title: "# of Applications Approved",
          value: nominationsReport["ufNumberOfApplicationsApproved"],
          tooltip: "Number of applications approved.",
          bottomBorder: false,
          display: true,
        },
      ]
    },
    participantSummaryDescriptions(participantSummaryReport) {
      return [
        {
          title: "# of Participants Dropped Out",
          value: participantSummaryReport["numberOfDropOutParticipants"],
          tooltip: "The total number of participants who have dropped out of the program.",
          bottomBorder: false,
          display: true,
        },
      ]
    },
    nominationsReportDescriptions(nominationsReport) {
      return [
        {
          title: "# of Nominations",
          value: nominationsReport["totalNumberOfNominations"],
          tooltip: "The total number of nominations for the Upward Fund.",
          bottomBorder: false,
          display: true,
        },
        {
          title: "Currently in Phase 1",
          value: nominationsReport["currentlyInPhaseOne"],
          tooltip: "The number of nominations in Phase 1.",
          bottomBorder: false,
          display: true,
        },
        {
          title: "Currently in Phase 2",
          value: nominationsReport["currentlyInPhaseTwo"],
          tooltip: "The number of nominees that made it to Phase 2.",
          bottomBorder: true,
          display: true,
        },
        {
          title: "Decisioned: Enrolled",
          value: nominationsReport["decisionedEnrolled"],
          tooltip: "The number of participants whose applications were approved.",
          bottomBorder: false,
          display: true,
        },
        {
          title: "Decisioned: Declined in Phase 1",
          value: nominationsReport["decisionedDeclinedPhaseOne"],
          tooltip: "The number of nominations declined in Phase 1.",
          bottomBorder: false,
          display: true,
        },
        {
          title: "Decisioned: Declined in Phase 2",
          value: nominationsReport["decisionedDeclinedPhaseTwo"],
          tooltip: "The number of applicants declined in Phase 2.",
          bottomBorder: true,
          display: true,
        },
      ]
    },
    async refresh() {
      await this.organizations.refresh()


      if (this.user.isStaff) {
          this.selectedOrg = "all"
          this.selectedSubOrganization = null
      }

      if (this.user.isAdmin && !this.user.subOrganization) {
          this.selectedOrg = this.user.organizationRef.id
          this.selectedSubOrganization = null
      }

      if (this.suborganizations().length > 0 && this.user.subOrganization) {
          this.selectedOrg = this.user.organizationRef.id
          this.selectedSubOrganization = this.suborganizations()[0].id
      }

      this.reloadReports()
    },
    isMultiSubOrgOwner() {
      if (this.user.isStaff) {
          return true
      }

      if (this.user.isAdmin && !this.user.subOrganization) {
          return true
      }

      if (this.suborganizations().length > 0 && this.user.subOrganization) {
          return false
      }
    },
    selectTab(tabName) {
      this.selectedTabName = tabName

      if (tabName === 'allOrg' ) {
        this.trackCustomPageview('AdminReportsRequests', '/reportsAllOrg')
      }
      if (tabName === 'requests' ) {
        this.trackCustomPageview('AdminReportsRequests', '/reportsRequests')
      }
      if (tabName === 'users' ) {
        this.trackCustomPageview('AdminReportsUsers', '/reportsUsers')
      }
      if (tabName === 'contributions' ) {
        this.trackCustomPageview('AdminReportsContributions', '/reportsContributions')
      }
      if (tabName === 'trendData' ) {
        this.trackCustomPageview('AdminReportsTrend', '/reportsTrend')
      }
      if (tabName === 'upwardFund' ) {
        this.trackCustomPageview('AdminReportsUpwardFund', '/reportsUpwardFund')
      }
      if (tabName === 'northwestern' ) {
        this.trackCustomPageview('AdminReportsNorthwestern', '/reportsNorthwestern')
      }
    },
    selectedTab(tabName) {
      if (tabName == this.selectedTabName) {
          return "tab selected"
      }
      return "tab"
    },
    dateRangeChanged(value) {
      if (value == this.selectedDateRange) { return; }

      this.selectedDateRange = value;

      switch (this.selectedDateRange) {
        case "all_time":
          this.isCustomDatePickerVisible = false;
          this.setReportFilter(null);
          break;

        case "year_to_date":
          this.isCustomDatePickerVisible = false;
          const now = new Date(Date.now());
          const thisYear = new Date(Date.now());

          thisYear.setFullYear(new Date().getFullYear(), 0, 1);

          const filter = {
            startDate: thisYear,
            endDate: now
          };

          this.setReportFilter(filter);
          break;

        case "custom":
          this.isCustomDatePickerVisible = true;
          this.buildCustomDateRangeFilter();
          break;
      }
    },
    buildCustomDateRangeFilter() {
      const start = this.selectedStartDate;
      const end = this.selectedEndDate;

      if (!start || !end) {
        return
      }

      const startTimestamp = Date.parse(start);
      const endTimestamp = Date.parse(end);
      if (isNaN(startTimestamp) || isNaN(endTimestamp)) {
        return;
      }

      const startDate = new Date(startTimestamp);
      const endDate = new Date(endTimestamp);

      this.setReportFilter({ startDate, endDate });
    },
    dateValueChanged(newValue, oldValue) {
      // Work around md-datepicker giving us a string instead of a Date
      const newTimestamp = newValue ? Date.parse(newValue) : null;
      const oldTimestamp = oldValue ? Date.parse(oldValue) : null;
      if (isNaN(newTimestamp) || isNaN(oldTimestamp)) { return }

      // Work around md-datepicker firing two identical events for a single
      // change event.
      // See https://github.com/vuematerial/vue-material/issues/2103
      if (oldTimestamp == newTimestamp) { return }

      const newDate = new Date(newTimestamp);
      const oldDate = new Date(oldTimestamp);

      // Work around md-datepicker triggering two watch events with different
      // timezone data for the same change event
      if (
        oldTimestamp && newTimestamp &&
        (
          (new Date(oldTimestamp)).toDateString() == (new Date(newTimestamp)).toDateString()
        )
      ) {
        return;
      }

      this.buildCustomDateRangeFilter();
    },
    setReportFilter(filter) {
      // Setting null value clears the filter
      if (!filter) {
        this.reportFilter = null;
        return;
      }

      // Ignore incomplete filter
      if (!filter.startDate || !filter.endDate) {
        return;
      }

      // Ignore invalid filter
      if (filter.startDate > filter.endDate) {
        // @TODO: set validation error in UI
        return;
      }

      // Ignore new filter of same value as existing
      if (
        this.reportFilter &&
        filter.startDate == this.reportFilter.startDate &&
        filter.endDate == this.reportFilter.endDate
      ) {
        return;
      }

      this.reportFilter = filter;
    },
    fetchReport(org, suborg, filter) {
      this.loading = true
      this.chartsLoading = true
      this.report = null
      Report.api
        .retrieve(org, suborg, filter)
        .then((report) => {
          this.report = report
        })

      this.getOrgChartData(org, suborg, filter);
    },
    getOrgChartData(newOrg, suborg, filter) {
      this.charts = {}
      var chartFuncs = [this.getDeclineReasons, this.getEvents, this.getExpenses]

      if(newOrg !== 'all')
        chartFuncs.push(this.getNominatingSubOrgs)

      this.chartsLoading = true
      Promise.all(chartFuncs.map((func) => func(newOrg, suborg, filter))).then(() => {
        this.chartsLoading = false
      })
    },
    async getGoalProgressionData() {
      const goalProgressionChartData = await OrgChart.api.tcrGoalProgressChartData()
      this.goalProgressionData = goalProgressionChartData
    },
    async getProgramMonthData() {
      const programMonthChartData = await OrgChart.api.tcrProgramMonthChartData()
      this.programMonthData = programMonthChartData
    },
    async getParticipantDropoutData() {
      const participantDropoutChartData = await OrgChart.api.tcrParticipantDropoutChartData()
      this.participantDropoutData = participantDropoutChartData
    },
    async getParticipantDropoutReasonData() {
      const participantDropoutReasonChartData = await OrgChart.api.tcrParticipantDropoutReasonChartData()
      this.participantDropoutReasonData = participantDropoutReasonChartData
    },
    getNominationsReport(org, suborg, filter) {
      this.loading = true
      this.nominatorReportLoading = true
      Report.api
        .retrieveNominations(org, suborg, this.selectedNominator, filter)
        .then((nominationsReport) => {
          this.nominationsReport = nominationsReport
        }).finally(this.nominatorReportLoading = false)
    },
    getParticipantSummaryReport(org, suborg, filter) {
      this.loading = true
      Report.api
        .retreiveParticipantSummary(org, suborg, this.selectedNominator, filter)
        .then((participantSummaryReport) => {
          this.participantSummaryReport = participantSummaryReport
        })
    },
    getMetricChartData(selectedOrganization, selectedSubOrganization, selectedMetric, selectedTimeFrame) {
      return OrgChart.api.metricChartData(selectedOrganization, selectedSubOrganization, selectedMetric, selectedTimeFrame).then((metricData) => {
        const trendData = Object.entries(metricData)

        var yAxisName = 'y'
        var index = 0

        if (selectedTimeFrame === 'yearly') {
          const trendLabels = [...new Set(trendData.map((datum) => datum[0]))]

          const trendedDateFormatted = trendData.reduce((previous, current, currentIndex) => {
              const datumDate = new Date(current[0])
              const year = datumDate.toLocaleString('default', { timeZone: 'utc', year: 'numeric' })

              const colors = [ '#1D5A5F', '#47B393', '#FEDA67', '#FEF1C5', '#08262A' ]

              yAxisName = `y`

              if (previous[0] && Object.keys(previous[0]).includes('data')) {
                previous[0]['data'].push({ x: year, [yAxisName]: current[1] })
              } else {
                const label = this.metricPrefixes[this.selectedMetric] ? `${metricNames[this.selectedMetric]} ($)` : `# ${metricNames[this.selectedMetric]}`
                previous[0] = { 'data': [], 'yAxisID': yAxisName, label: label, borderColor: colors[0],  backgroundColor: colors[0]  }
                previous[0]['data'].push({ x: year, [yAxisName]: current[1] })
              }

              return previous
          },
          []
          )

          this.trendChart.data.labels = trendLabels
          this.trendChart.data.datasets = trendedDateFormatted
          this.chartKey += 1
        }

        if (selectedTimeFrame === 'monthly') {
          const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
          const trendLabels = [...new Set(trendData.map((datum) => datum[0]))]

          const trendedDateFormatted = trendData.reduce((previous, current, currentIndex) => {
              const datumDate = new Date(current[0])
              const originalMonthNumber = datumDate.getMonth()
              const month = datumDate.toLocaleString('default', { timeZone: 'utc', month: 'long' })
              const year = datumDate.toLocaleString('default', { timeZone: 'utc', year: 'numeric' })

              const colors = [ '#1D5A5F', '#47B393', '#FEDA67', '#FEF1C5', '#08262A' ]

              if (currentIndex == 0) {
                previous[index] = { 'data': [], 'yAxisID': yAxisName, label: year, borderColor: colors[index],  backgroundColor: colors[index] }
              }

              if (currentIndex == 0 && month != 'January') {
                  previous[index] = { 'data': [], 'yAxisID': yAxisName, label: year, borderColor: colors[index],  backgroundColor: colors[index] }

                  for (const monthNumber in [...Array(originalMonthNumber + 1).keys()]) {
                    previous[index]['data'].push({ x: months[monthNumber], [yAxisName]: 0 })
                  }
              }

              if (month == 'January') {
                yAxisName = `y`
                index = index + 1
                previous[index] = { 'data': [], 'yAxisID': yAxisName, label: year, borderColor: colors[index],  backgroundColor: colors[index]  }
              }

              if (previous[index] && Object.keys(previous[index]).includes('data')) {
                previous[index]['data'].push({ x: month, [yAxisName]: current[1] })
              } else {
                previous[index] = {'data': []}
                previous[index]['data'].push({ x: month, [yAxisName]: current[1] })
              }

              return previous
          },
          []
          )

          this.trendChart.data.labels = months
          this.trendChart.data.datasets = trendedDateFormatted
          this.chartKey += 1
        }

        this.trendChart.title = this.metricNames[this.selectedMetric]
        this.trendChart.timeframe = this.selectedTimeFrame
      })
    },
    getDeclineReasons(org, subOrg, filter) {
      return OrgChart.api.declines(org, subOrg, filter).then((events) => {
        const labels = []
        const percentages = []
        const totals = []
        Object.keys(events.data).forEach((label) => {
          let combinedLabel = ""
          let labelSplit = label.split(" - ")
          if(labelSplit.length > 1) {
            combinedLabel = decline_reason_labels[labelSplit[0]] + " - " + decline_reason_details_labels[labelSplit[1]]
          } else {
            combinedLabel = decline_reason_labels[labelSplit[0]]
          }
          labels.push(combinedLabel)
          percentages.push(events.data[label]['percent'])
          totals.push(events.data[label]['total'])
        })
        this.charts["decline"] = {
          name: 'chart3',
          title: 'Decline reasons',
          subtitle: '(as % of declined requests)',
          tooltip: 'Reasons requests were declined, expressed as a fraction of total requests declined',
          data: {
            labels: labels,
            datasets: [
              {
                data: percentages,
                backgroundColor: '#1D5A5F',
              },
            ],
          },
        }
      })
    },
    getEvents(org, subOrg, filter) {
      return OrgChart.api.events(org, subOrg, filter).then((events) => {
        const labels = []
        const percentages = []
        events.data.forEach((event) => {
          const shortenedLabel = event.label.split('(')[0]
          labels.push(shortenedLabel)
          percentages.push(event.percentage)
        })
        this.charts['events'] = {
          name: 'chart1',
          title: 'Precipitating events',
          subtitle: '(as % of total requests, multi-select possible)',
          tooltip: 'Emergency event types selected in grant requests, expressed as a fraction of total requests, multi-select possible',
          data: {
            labels: labels,
            datasets: [
              {
                data: percentages,
                backgroundColor: '#1D5A5F',
              },
            ],
          },
        }
      })
    },
    getExpenses(org, subOrg, filter) {
      return OrgChart.api.expenses(org, subOrg, filter).then((expenses) => {
        const labels = []
        const percentages = []
        expenses.data.forEach((expense) => {
          const shortenedLabel = expense.label.split('(')[0]
          labels.push(shortenedLabel)
          percentages.push(expense.percentage)
        })
        this.charts['expenses'] = {
          name: 'chart2',
          title: 'Expenses',
          tooltip: 'Expense types selected in grant requests, expressed as a fraction of total requests, multi-select possible',
          subtitle: '(as % of total requests, multi-select possible)',
          data: {
            labels: labels,
            datasets: [
              {
                data: percentages,
                backgroundColor: '#FEDA67',
              },
            ],
          },
        }
      })
    },
    getNominatingSubOrgs(org, subOrg, filter) {
      return OrgChart.api.nominatingSubOrgs(org, subOrg, filter).then((nominatingSubOrgs) => {
        const labels = []
        const percentages = []
        const values = []
        nominatingSubOrgs.data.forEach((nominatingSubOrg) => {
          labels.push(nominatingSubOrg.name)
          values.push(nominatingSubOrg.count)
          percentages.push(nominatingSubOrg.percentage)
        })
        this.charts['nominatingSubOrgs'] = {
          name: 'chart4',
          title: 'Nominating Organizations',
          tooltip: 'Total enrolled participants by organization.',
          subtitle: '(as % of total enrolled participants)',
          data: {
            labels: labels,
            values: values,
            datasets: [
              {
                data: percentages,
                backgroundColor: '#1D5A5F',
              },
            ],
          },
        }
      })
    },
    onChangedTab(newTab) {
      this.selectedTabName = newTab
    },
    exportDataset() {
      if (!this.selectedDataset) {
        return
      }
      this.exporting = true
      Report.api.exportDataset(this.selectedDataset, this.reportFilter).finally(() => {
        this.exporting = false
      })
    },
    exportAll() {
      this.exporting = true
      const datasets = this.exportableDatasets

      const promises = datasets.map((dataset) => {
        return Report.api
          .exportDataset(dataset.value)
          .then((response) => {
            let hiddenElement = document.createElement('a')
            hiddenElement.href = 'data:text/csv;charset=utf-8,' + encodeURI(response.data)
            hiddenElement.target = '_blank'
            hiddenElement.download = `${dataset.value}_DATASET__${new Date().toUTCString()}.csv`
            hiddenElement.click()
          })
          .catch((err) => {
            this.exporting = false
          })
      })

      Promise.all(promises).then((responses) => {
        this.exporting = false
      })
    },

    async reloadReports() {
      this.loading = true

      this.selectedDataset = null

      if (this.selectedOrg == "all") {
        this.fetchReport(this.selectedOrg || this.user.organizationRef.id, null, this.reportFilter)
        this.selectedTabName = "allOrg"
        this.loading = false
        return
      }

      if (this.selectedOrg && this.suborganizations().length > 0 && this.selectedSubOrganization) {
        this.fetchReport(this.selectedOrg, this.selectedSubOrganization, this.reportFilter);
        this.getMetricChartData(this.selectedOrg, this.selectedSubOrganization, this.selectedMetric, this.selectedTimeFrame)
      }

      if (this.selectedOrg && !this.user.subOrganization && this.suborganizations().length === 0) {
        this.fetchReport(this.selectedOrg, null, this.reportFilter);
        this.getMetricChartData(this.selectedOrg, null, this.selectedMetric, this.selectedTimeFrame)
      }

      if (this.selectedOrg && this.isMultiSubOrgOwner() && !this.selectedSubOrganization) {
        this.fetchReport(this.selectedOrg, null, this.reportFilter);
        this.getMetricChartData(this.selectedOrg, null, this.selectedMetric, this.selectedTimeFrame)
      }

      // Get our select org reference, if there is one
      let selectedOrgRef = null
      if(this.selectedOrg)
        selectedOrgRef = this.organizations.list.find(org => org.id === this.selectedOrg)

      // TCR reporting & charts
      if((selectedOrgRef && selectedOrgRef.isTcrOrganization) || (this.user && this.user.organizationRef && this.user.organizationRef.isTcrOrganization)) {

        // Set the organization if admin
        if (!(selectedOrgRef && selectedOrgRef.isTcrOrganization)) {
          selectedOrgRef = this.user.organizationRef
          this.selectedOrg = this.user.organizationRef.id
        }

        this.fetchReport(this.selectedOrg, this.selectedSubOrganization, this.reportFilter);
        await this.getNominationsReport(this.selectedOrg, this.selectedSubOrganization, this.reportFilter)
        await this.getParticipantSummaryReport(this.selectedOrg, this.selectedSubOrganization, this.reportFilter)
        await this.getGoalProgressionData()
        await this.getProgramMonthData()
        await this.getParticipantDropoutData()
        await this.getParticipantDropoutReasonData()
      }

      // Select default tab
      if (this.user.isStaff && this.selectedOrg == "all") {
        this.selectedTabName = "allOrg"
      } else if (this.user.isStaff && this.selectedOrg && selectedOrgRef && selectedOrgRef.isTcrOrganization) {
        // Staff user w/ selected Upward Fund
        this.selectedTabName = "upwardFund"
      } else if (this.user.isAdmin && this.user.organizationRef.isTcrOrganization) {
        // Admin user for the Upward Fund
        this.selectedTabName = "upwardFund"
      } else if (this.user.isStaff && this.selectedOrg && selectedOrgRef && selectedOrgRef.isNorthwestern) {
        this.selectedTabName = "northwestern"
      } else if (this.user.isAdmin && this.user.organizationRef.isNorthwestern) {
        this.selectedTabName = "northwestern"
      } else {
        // Default
        this.selectedTabName = "requests"
      }
      this.loading = false
    },
    async reloadNominatorReport() {
      await this.getNominationsReport(this.selectedOrg, this.selectedSubOrganization, this.reportFilter)
      this.loading = false
    },
    toggleShowEnrolledParticipants() {
      if(this.showEnrolledParticipants) {
        this.showEnrolledParticipants = false
      } else {
        this.showEnrolledParticipants = true
      }
    },
    toggleShowDeclinedNominees() {
      if(this.showDeclinedNominees) {
        this.showDeclinedNominees = false
      } else {
        this.showDeclinedNominees = true
      }
    },
    toggleShowMoneyDueToNominators() {
      if(this.showMoneyDueToNominators) {
        this.showMoneyDueToNominators = false
      } else {
        this.showMoneyDueToNominators = true
      }
    },
    toggleShowAtRiskParticipants() {
      if(this.showAtRiskParticipants) {
        this.showAtRiskParticipants = false
      } else {
        this.showAtRiskParticipants = true
      }
    },
    toggleShowAtRiskParticipantsPast() {
      if(this.showAtRiskParticipantsPast) {
        this.showAtRiskParticipantsPast = false
      } else {
        this.showAtRiskParticipantsPast = true
      }
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails']),
    isStaffMember() {
      return this.$store.state.user.isStaff
    },
    exportableDatasets() {
      let additionalDatasets = []

      /* School Heroes */
      if (this.showShefMetrics) {
        additionalDatasets = additionalDatasets.concat(Report.options.SHEF_DATASETS)

        if(this.user.isAdmin && this.user.adminPermissions && this.user.adminPermissions.name === AdminPermissions.permissionNames.TCR_ADMIN)
          return additionalDatasets
      }

      /* Northwestern */
      if (this.showNorthwesternTab) {
        additionalDatasets = additionalDatasets.concat(Report.options.NORTHWESTERN_DATASETS)

        if(this.user.isAdmin && this.user.adminPermissions && this.user.adminPermissions.name === AdminPermissions.permissionNames.NORTHWESTERN)
          return additionalDatasets
      }

      /* Upward Fund */
      if (this.showUpwardFundTab) {
        additionalDatasets = additionalDatasets.concat(Report.options.UF_DATASETS)

        if(this.user.isAdmin && this.user.adminPermissions && this.user.adminPermissions.name === AdminPermissions.permissionNames.TCR_ADMIN)
          return additionalDatasets
      }

      /* Community Heroes */
      if (this.showChfMetrics) {
        additionalDatasets = additionalDatasets.concat(Report.options.CHF_DATASETS)

        if(this.user.isAdmin && this.user.adminPermissions && this.user.adminPermissions.name === AdminPermissions.permissionNames.TCR_ADMIN)
          return additionalDatasets
      }

      if (this.isStaffMember) {
        return additionalDatasets.concat(Report.options.STAFF_ONLY_DATASETS.concat(Report.options.EXPORTABLE_DATASETS))
      }

      return additionalDatasets.concat(Report.options.EXPORTABLE_DATASETS)
    },
    selectedOrgObject() {
      if (this.selectedOrg === "all") {
        return null
      }

      return this.organizations.list.find((org) => org.id === this.selectedOrg)
    },
    isReferralClient() {
      return this.selectedOrgObject && this.selectedOrgObject.isReferralOrganization
    },
    dateNow() {
      const now = new Date();

      const yyyy = now.getFullYear().toString(10);
      const month = (now.getMonth() + 1).toString(10);
      const day = now.getDate().toString(10);

      const mm = month.length == 1 ? `0${month}` : month;
      const dd = day.length == 1 ? `0${day}` : day;

      return mm + '/' + dd + '/' + yyyy;
    },
    showUpwardFundTab() {
      // No organization selected
      if(!this.selectedOrg)
        return false

      // Determine if our selected organization is TCR organization
      let isTcrOrganization = this.organizations.list.find(organizationDetails => organizationDetails.id == this.selectedOrg && organizationDetails.isTcrOrganization)
      if(!isTcrOrganization && this.isStaffMember) {
        return false
      }

      // If the user is staff or a TCR admin show the tab
      if(isTcrOrganization && (this.isStaffMember || (this.user.isAdmin && this.user.adminPermissions.name === AdminPermissions.permissionNames.TCR_ADMIN)))
        return true

      return false
    },
    showNorthwesternTab() {
      // No organization selected
      if(!this.selectedOrg)
        return false

      // Determine if our selected organization is TCR organization
      let isNorthwestern = this.organizations.list.find(organizationDetails => organizationDetails.id == this.selectedOrg && organizationDetails.isNorthwestern)
      if(!isNorthwestern && this.isStaffMember) {
        return false
      }

      // If the user is staff or a TCR admin show the tab
      if(isNorthwestern && (this.isStaffMember || (this.user.isAdmin && this.user.adminPermissions.name === AdminPermissions.permissionNames.NORTHWESTERN)))
        return true

      return false
    },
    showShefMetrics() {
      // No organization selected
      if(!this.selectedOrg)
        return false

      // Determine if our selected organization is TCR organization
      let isTcrShef = this.organizations.list.find(organizationDetails => organizationDetails.id == this.selectedOrg && organizationDetails.isTcrShef)
      if(!isTcrShef && this.isStaffMember) {
        return false
      }

      // If the user is staff or a TCR admin show the tab
      if(isTcrShef && (this.isStaffMember || this.user.isAdmin))
        return true

      return false
    },
    showChfMetrics() {
      // No organization selected
      if(!this.selectedOrg)
        return false

      // Determine if our selected organization is TCR organization
      let isTcrChf = this.organizations.list.find(organizationDetails => organizationDetails.id == this.selectedOrg && organizationDetails.isTcrChf)
      if(!isTcrChf && this.isStaffMember) {
        return false
      }

      // If the user is staff or a TCR admin show the tab
      if(isTcrChf && (this.isStaffMember || this.user.isAdmin))
        return true

      return false
    },
    isSelfManagedPayrollDonations() {
      if(!this.selectedOrgObject)
        return false

      return this.clientsSelfManagingPayrollDonations.includes(this.selectedOrgObject.name)
    },
  },
  watch: {
    selectedStartDate(newStart, oldStart) {
      this.dateValueChanged(newStart, oldStart);
    },
    selectedEndDate(newEnd, oldEnd) {
      this.dateValueChanged(newEnd, oldEnd);
    },
    reportFilter(newFilter) {
      this.reloadReports()
    },
    selectedMetric(newVal) {
      this.getMetricChartData(this.selectedOrg, this.selectedSubOrganization, newVal, this.selectedTimeFrame)
    },
    selectedTimeFrame(newVal) {
      this.getMetricChartData(this.selectedOrg, this.selectedSubOrganization, this.selectedMetric, newVal)
    }
  },
}
</script>

<style lang="scss" scoped>
@import '../../styles/variables';
@import '../../styles/mixins/cards';
@import '../../styles/mixins/containers';


.FundBalance > .FundBalance__heading {
  margin-bottom: 0px;
}

.FundBalance > .FundBalance__subtitle {
  font-size: 12px;
  color: #919EAB;
  margin: 8px 0 0 0;
  font-weight: 600;
}

.organization-name {
  color: #212B36;
  font-size: 36px;
  margin-top: 2rem;
  font-weight: bold;
}

.staff-reports {
  @include screen-container;
}

.header-container {
  display: flex;
  justify-content: space-between;
  align-self: stretch;
}

.Report__Filter__DateRange {
  margin-bottom: 24px;
}

.md-override.Report__Filter__DateRange-field {
  margin: 8px 0 0 0;
  padding: 0;
}

.md-override.Report__Filter__DateRange-picker {
  margin: 0;
}

.stat-container {
  display: flex;
  margin-left: 1.5rem;
  margin-right: 1.5rem;
  flex-wrap: wrap;
  min-width: 250px;
  border-radius: 12px;
  box-shadow: 0 0 0 1px rgba(63,63,68,0.05), 0 1px 3px 0 rgba(63,63,68,0.15);

  .tab-name {
    text-align: unset;
    padding: 1.5rem;
    font-weight: bold;
  }

  .table-headings {
    display: flex;
    margin-left: 1.5rem;
    margin-right: 1.5rem;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    border-bottom-width: 2px;
    border-bottom-color: #DFDFE0;
    border-bottom-style: solid;

    &.no-margins {
      margin-top: 1.5rem;
      margin-left: 0;
      margin-right: 0;
    }

    .header {
      font-size: 16px;
      font-weight: bold;
    }
  }

  .standard-tab {
    font-size: 12px;
    font-weight: 600;
    color: #637381;
  }

  .trend-data {
    width: 100%;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  .selected-tab {
    font-size: 12px;
    font-weight: 600;
    color: #212B36;
  }

  .requests, .nominations {
    width: 50%;
  }

  .new-chart {
    border-radius: 12px;
    margin-top: 1.5rem;
    padding: 1.5rem;
    box-shadow: 0 0 0 1px rgba(63,63,68,0.05), 0 1px 3px 0 rgba(63,63,68,0.15);
    border-style: solid;
    border-width: 1px;
    border-color: #DFDFE0;

    &__title_and_tooltip {
      margin-bottom: 0.5rem;
      display: flex;
      flex-direction: row;
      align-items: center;
    }

    &__subtitle {
      font-size: 12px;
      color: #919EAB;
      display: inline-block;
      margin-bottom: 1rem;
    }

    &__title {
      margin-bottom: 0;
      color: $black;
      font-weight: bold;
    }

    .chart-pill {
      display: flex;
      flex-direction: column;
    }

    .pill-title {
      font-size: 12px;
      max-width: 200px;
    }

    .pill {
      height: 12px;
      border-radius: 12px;
      border-color: #DFDFE0;
      border-width: 1px;
      border-style: solid;
      background-color: rgba(0,0,0,0.1);
      width: 100%;
      display: flex;
      align-items: center;
    }

    .pill-percent-wrapper {
      width: 100%;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      margin-top: .5rem;
      margin-bottom: .15rem;
    }

    .pill-percentage {
      font-size: 12px;
      font-weight: 700;
    }

    .pill-data-extra {
      font-size: 12px;
      margin-left: .25rem;
      font-weight: 700;
    }

    .pill-title-wrapper {
      font-size: 12px;
      display: flex;
      font-weight: 700;
    }

    .pill-fill {
      height: 12px;
      border-radius: 12px;
      border-style: solid;
      border-width: 1px;
      border-color: #1D5A5F;
      background-color: #1D5A5F;
    }
  }

  .first-col-charts {
    margin-bottom: 1.5rem;
  }

  .second-col-charts {
    margin-bottom: 1.5rem;
  }

  .all-charts {
    width: 50%;
  }

  .bar-charts {
    padding: 1.5rem;
    margin: 1.5rem 1.5rem 0 1.5rem;

    .goal-question {
      padding-top: 1.5rem;
      padding-bottom: 1.5rem;
      max-width: unset;
    }
  }

  .charts {
    width: 100%;
    display: grid;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
  }
}

.stat-card-container {
  display: flex;
  flex-wrap: wrap;
  min-width: 250px;

  @media (min-width: $mobile-break-point) {
    display: flex;
    flex-wrap: wrap;
    min-width: 250px;
    width: 100%;
    height: 100%;
  }

  .stacked-stat-cards {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-width: 250px;

    .stacked-stat-card {
      @include card;
      width: 100%;
      padding: 1.5rem;
      width: 100%;
      height: 100%;
      border-radius: 12px;
      box-shadow: 0 0 0 1px rgba(63,63,68,0.05), 0 1px 3px 0 rgba(63,63,68,0.15);

      &.bottom {
        margin-top: 1.5rem
      }


      & > p {
        margin: 1rem 0;
      }

      & strong {
        font-weight: 700;
      }
    }
  }
}

.stat-card-wrapper {
  width: 33.33%;
  padding: 1.5rem;
  display: flex;
  min-width: 250px;
}

.icon-and-tooltip {
  display: flex;
  flex-direction: row;
  align-items: center;

  p {
    font-weight: bold;
  }
}

.tooltip {
  max-width: 250px;
  background-color: black;
  color: white;
}

.stat-card {
  @include card;
  padding: 1.5rem;
  min-width: 250px;
  width: 100%;
  height: 100%;
  border-radius: 12px;
  box-shadow: 0 0 0 1px rgba(63,63,68,0.05), 0 1px 3px 0 rgba(63,63,68,0.15);

  & > p {
    margin: 1rem 0;
  }

  & strong {
    font-weight: 700;
  }
}

.tab-container {
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  display: flex;
  flex-direction: row;
  width: 100%;
  border-bottom: solid;
  border-bottom-width: 1px;
  border-bottom-color: #DFDFE0;
}

.tab {
  padding: 1.5rem;
  cursor: pointer;

  &.selected {
    border-bottom: solid;
    border-bottom-width: 1px;
    border-bottom-color: black;
  }
}

.stat-list {
  padding: 1.5rem;
}


.trends {
  width: 100%;

  .trend-data-tab {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
  }

  .trend-data-selection {
    width: 40%;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
}

.barchart {
  margin: 1.5rem;
  width: 60%;
}

.chart {
  margin: 1.5rem;
  width: 60%;
}

.md-field {
  max-width: 25rem;
  margin-right: 3rem;
  margin-bottom: 0;
}

.forms {
  display: flex;
  flex-flow: row;
  margin-bottom: 24px;
  padding-left: 1.5rem;
  padding-right: 1.5rem;

  .exports {
    margin-left: auto;
    display: flex;
    flex-flow: row;
    align-items: center;

    .md-button {
      width: 8rem;
    }
  }
}

.md-tooltip {
  height: auto;
  white-space: normal;
  font-size: 12px;
  max-width: 300px;
}

.enrolled-participants, .declined-nominees, .money-due-to-nominators, .at-risk-participants {
  margin-bottom: 1.5rem;
}

.enrolled-participants, .declined-nominees, .money-due-to-nominators, .at-risk-participants, .at-risk-participants-past {
  h2 {
    margin-bottom: 0;
  }
}

.participant-list, .nominee-list, .nominator-list {
  padding: 1.5rem 0 0 0;
}

.participant, .nominee, .nominator {
  padding-bottom: 0.5rem;
  margin-bottom: 0.5rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.stat-description-wrapper {
  display: flex;
  flex-direction: row;
  width: 25%;
}

.collapsible-header {
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  align-items: center;
}

.no-data {
  margin-top: 1rem;
}

.nominator-filter {
  margin-left: 1.5rem;
  margin-bottom: 1.5rem;
}
</style>
